/**
 * The two pure rules behind a coach changing an exercise's intensity days by hand (#6428):
 *
 * - `buildSchemeSwitch`: the coach picks One Target, DUP or TUP and types the rep targets. The new
 *   days get their starting weights from the client's best recent set, so the next session is a
 *   normal one instead of a strength test.
 * - `deloadMemoryAfterHandWeightChange`: the coach types a different Next Weight. That counts as a
 *   deload, so the strength the plateau check compares against is taken again from the client's
 *   most recent session on that day.
 *
 * One example runs through both (and through the tests). Alex, an Advanced client, best recent
 * Bench Press set 80 kg x 8, which the app reads as a one-rep max of 100.9 kg. His coach switches
 * him to DUP with heavy 6 and light 10: heavy 6 reps is 84.94 kg, rounded DOWN on a heavy day to
 * 82.5 kg; light 10 reps is 75.43 kg, rounded to 75 kg. Before any switch, his coach lowers his next
 * weight from 77.5 to 72.5 kg: his last session was 77.5 kg x 7, so the remembered strength becomes
 * 94.9 kg and a plateau is called below 99.7 kg.
 *
 * Pure: no Angular, no database. The rules and the owner's decisions:
 * `design-docs/coach-exercise-editing.md`.
 */

import { IntensityLevel, Plan_ExercisePath } from '@cf-shared/data-model/fitness-plan';
import { TrainingLevel } from '@cf-shared/data-model/training-level';
import { calculate1RM, estimateWeightForReps, SetStats } from '@cf-shared/data-model/set-stats';
import { adjustWeight, Equipment } from '@cf-shared/data-model/equipment';
import { Log_WorkoutExercise } from '@cf-shared/data-model/log-workout';
import { SetType } from '@cf-shared/enums';
import { bestStrengthSetOfSession, ROUND_DOWN_FROM_INTENSITY_PC } from 'pages/workout-detail/workout-progression-decision';

/** how many intensity days an exercise rotates through */
export enum IntensityScheme {
  OneTarget = 'OneTarget',
  Dup = 'Dup',
  Tup = 'Tup',
}

/**
 * Added before snapping a calculated weight onto the equipment. `estimateWeightForReps` run on the
 * one-rep max of a set gives back a hair under that set's weight (20 kg x 6 comes back as
 * 19.999999999999996 kg), and `adjustWeight`'s manual-equipment branch truncates with no tolerance,
 * so without this a round-down would drop a whole plate step.
 */
const SNAP_EPSILON_KG = 1e-6;

/** the scheme a sub-slot's paths form today; no paths reads as One Target */
export function schemeOfPaths(paths: readonly Plan_ExercisePath[] | undefined): IntensityScheme {
  const levels = (paths ?? []).map(path => path?.intensityLevel);
  if (levels.some(level => level === IntensityLevel.TUPheavy || level === IntensityLevel.TUPmedium || level === IntensityLevel.TUPlight)) {
    return IntensityScheme.Tup;
  }
  if (levels.some(level => level === IntensityLevel.DUPheavy || level === IntensityLevel.DUPlight)) {
    return IntensityScheme.Dup;
  }
  return IntensityScheme.OneTarget;
}

/** the scheme a pending rebuild (`rebuildPathsForIntensity`) will produce */
export function schemeOfLevel(level: IntensityLevel): IntensityScheme {
  if (level >= IntensityLevel.TUPlight && level <= IntensityLevel.TUPheavy) return IntensityScheme.Tup;
  if (level >= IntensityLevel.DUPlight && level <= IntensityLevel.DUPheavy) return IntensityScheme.Dup;
  return IntensityScheme.OneTarget;
}

/**
 * The schemes a training level offers, the same gates the intake form uses: a Novice trains on one
 * target, an Intermediate may also train heavy and light days, an Advanced lifter all three.
 */
export function schemesForTrainingLevel(trainingLevel: TrainingLevel): IntensityScheme[] {
  switch (trainingLevel) {
    case TrainingLevel.Advanced: return [IntensityScheme.OneTarget, IntensityScheme.Dup, IntensityScheme.Tup];
    case TrainingLevel.Intermediate: return [IntensityScheme.OneTarget, IntensityScheme.Dup];
    default: return [IntensityScheme.OneTarget];
  }
}

/**
 * The path levels of a scheme, heaviest first (the order the app's own rebuild uses). One Target
 * keeps the level the sub-slot already has when it is a one-target level (a Novice can be on
 * `Intensified`), and is `Initial` otherwise.
 */
export function levelsOfScheme(scheme: IntensityScheme, currentPaths?: readonly Plan_ExercisePath[]): IntensityLevel[] {
  switch (scheme) {
    case IntensityScheme.Tup: return [IntensityLevel.TUPheavy, IntensityLevel.TUPmedium, IntensityLevel.TUPlight];
    case IntensityScheme.Dup: return [IntensityLevel.DUPheavy, IntensityLevel.DUPlight];
    default: {
      const current = currentPaths?.length === 1 ? currentPaths[0]?.intensityLevel : undefined;
      return [current === IntensityLevel.Intensified ? IntensityLevel.Intensified : IntensityLevel.Initial];
    }
  }
}

/** everything `buildSchemeSwitch` needs; all of it read by the page before calling */
export interface SchemeSwitchInput {
  scheme: IntensityScheme;
  /** the levels of the new scheme, heaviest first (`levelsOfScheme`) */
  levels: IntensityLevel[];
  /** the rep target the coach set per level */
  repTargetsByLevel: { [level: number]: number };
  /** the share of the one-rep max each level trains at, in percent (`getSlotPathIntensity`) */
  intensityPcByLevel: { [level: number]: number | undefined };
  /** the client's best recent set (`bestStrengthSetAcrossSessions`), or undefined when there is none */
  bestRecentSet: SetStats | undefined;
  /** the client's body weight today */
  bodyWeight_kg: number;
  /** how much of this lift the client's own body carries, in percent */
  exerciseBw_pc: number;
  /** the client's equipment for this exercise; the weights are snapped onto it */
  equipment: Equipment;
}

/** what a scheme switch does to the sub-slot */
export type SchemeSwitchResult =
  | {
    kind: 'calculated';
    /** the new paths, heaviest first, each built fresh like a day that has never been trained */
    paths: Plan_ExercisePath[];
    /** the heaviest level: the day that comes next */
    nextWorkoutIntensity: IntensityLevel;
    /** the one-rep max the weights were worked out from */
    bestRecent1rm_net_kg: number;
  }
  | {
    /** nothing usable to calculate from: today's route, a strength test in the next session */
    kind: 'strengthTest';
    rebuildPathsForIntensity: IntensityLevel;
  };

/**
 * Builds the paths of a scheme the coach chose by hand.
 *
 * Each path is built fresh: the coach's rep target, a weight worked out from the best recent set
 * and snapped onto the equipment (rounded DOWN at or above 85% of the one-rep max, the same rule a
 * strength test's result uses), a normal first set, and nothing else. No minimum reps, no
 * remembered deload strength, no set adjustment and no pending volume question: exactly a day that
 * has never been trained.
 *
 * Falls back to a strength test (today's route) when there is no usable recent set, or when any
 * level's weight would come out undefined or not above zero, which happens when the one-rep max is
 * below the share of the lift the client's own body carries.
 */
export function buildSchemeSwitch(input: SchemeSwitchInput): SchemeSwitchResult {
  const strengthTest: SchemeSwitchResult = { kind: 'strengthTest', rebuildPathsForIntensity: input.levels[0] };
  const best = input.bestRecentSet;
  if (!best || !Number.isFinite(best.weight_kg) || !Number.isFinite(best.reps)) {
    return strengthTest;
  }
  const bw_pc = best.bw_pc ?? input.exerciseBw_pc;
  const bestRecent1rm_net_kg = calculate1RM(best.weight_kg, best.reps, input.bodyWeight_kg, bw_pc).estimated1rm_net_kg;

  const paths: Plan_ExercisePath[] = [];
  for (const level of input.levels) {
    const reps = input.repTargetsByLevel[level];
    const intensity_pc = input.intensityPcByLevel[level];
    const weight_kg = estimateWeightForReps(bestRecent1rm_net_kg, reps, input.bodyWeight_kg, input.exerciseBw_pc);
    // if this level cannot be worked out, no level is: a half-calculated scheme would be worse than a test
    if (weight_kg === undefined || !(weight_kg > 0) || intensity_pc === undefined) {
      return strengthTest;
    }
    const rounding = intensity_pc >= ROUND_DOWN_FROM_INTENSITY_PC ? 'round_down' : 'round';
    const snapped_kg = adjustWeight(weight_kg + SNAP_EPSILON_KG, rounding, input.equipment).weight_kg;
    paths.push({
      intensityLevel: level,
      nextWorkoutSetType: SetType.Regular1st,
      target_reps: reps,
      target_weight_kg: snapped_kg,
      orgTarget_weight_kg: snapped_kg,
      volumeAdjustment: 0,
    });
  }
  return { kind: 'calculated', paths, nextWorkoutIntensity: input.levels[0], bestRecent1rm_net_kg };
}

/**
 * The strength the plateau check should remember after the coach changed a day's Next Weight by
 * hand: the one-rep max of the best set of the client's most recent session on that day.
 *
 * Answers undefined when no such session exists, and the caller then leaves the remembered value
 * as it was. A raise and a lowering are treated the same.
 *
 * @param entriesNewestFirst this exercise's entry in each recent workout, newest first. An entry
 *   that records no intensity (written by an older app) counts for any day, as
 *   `isDeloadCarriedOverFromLastSession` treats it.
 */
export function deloadMemoryAfterHandWeightChange(
  entriesNewestFirst: readonly (Log_WorkoutExercise | null | undefined)[],
  intensityLevel: IntensityLevel,
  bodyWeight_kg: number,
  exerciseBw_pc: number,
): number | undefined {
  for (const entry of entriesNewestFirst) {
    if (!entry || (entry.pathLevel != null && entry.pathLevel !== intensityLevel)) continue;
    const best = bestStrengthSetOfSession(entry.sets, bodyWeight_kg, exerciseBw_pc);
    // a session with nothing usable (skipped, or only deload sets) says nothing; look further back
    if (!best) continue;
    return calculate1RM(best.weight_kg, best.reps, bodyWeight_kg, best.bw_pc ?? exerciseBw_pc).estimated1rm_net_kg;
  }
  return undefined;
}
